import Component_Model from  '@/nextts/model/Component_Model';
import Http_Interface from '@/nextts/play/Http_Interface';
import Play_Interface from '@/nextts/play/Play_Interface';
import gsap from "gsap";
import Content_Model from '@/nextts/model/Content_Model';
import { useContentStore } from "@/stores/content";
import Play_Api from './Play_Api';
import { emitter } from '@/emitter';
import Play_Sound from './Play_Sound';

class Play_SVG extends HTMLElement implements Http_Interface, Play_Interface{
    
    model:Component_Model|null = null;
    timeline:null|GSAPTimeline = null;

    contentStore = useContentStore();
    private updateTextHandler:any;
    private updateTextClickHandler:any;
    private updateTextTouchHandler:any;
    
    // SVG 캐시 (클래스 레벨에서 공유)
    private static svgCache = new Map<string, string>();
    private lastSvgPath = "";
    private lastColorSettings = "";

    constructor(m:Component_Model){
        super();
        this.model = m;
        this.style.position = "absolute";
    }
    
    init(): void {

        if(this.model?.showlock){
            return;
        }

        if(this.model != null && this.model.api_use){
            this.updateTextHandler = this.updatetext_handler.bind(this);
            emitter.on('updatetext', this.updateTextHandler);
        }

        if(this.model?.event_use){
            if(this.model.event_type >= 1){
                this.updateTextClickHandler = this.handleClick.bind(this);
                this.updateTextTouchHandler = this.handleTouchStart.bind(this);
                this.addEventListener('click',      this.updateTextClickHandler);
                this.addEventListener('touchstart', this.updateTextTouchHandler);                
            }
        }

        this.playAni();
    }

    handleClick(event: MouseEvent){
        this.clickevent();
    }
    
    handleTouchStart(event: TouchEvent){
        this.clickevent();
    }    

    clickevent(){
        if(this.model?.event_type == 1){
            for(let i=0; i < this.contentStore.content_model.screen_list.length; i++){
                const screen= this.contentStore.content_model.screen_list[i];
                if(this.model.event_move == screen.screen_name){
                    emitter.emit('movepage', i);
                    break;
                }
            }
        }else if(this.model?.event_type == 2){
            if(this.contentStore.select_screen == null) return;
            for(let i=0; i < this.contentStore.select_screen?.component_list.length; i++){
                const component = this.contentStore.select_screen.component_list[i];
                if(component.component_type == Component_Model.COMPONENT_API && this.model.event_api == component.apiname){
                    const api_component = component.play_component as Play_Api;
                    api_component.runApiAwait();
                    break;
                }
            }
        }else if(this.model?.event_type == 20){
            if(this.contentStore.select_screen == null) return;
            for(let i=0; i < this.contentStore.select_screen?.component_list.length; i++){
                const component = this.contentStore.select_screen.component_list[i];
                if(component.component_type == Component_Model.COMPONENT_SOUND && this.model.event_input_target == component.event_name){
                    const api_component = component.play_component as Play_Sound;
                    api_component.soundplayevent();
                    break;
                }
            }
        }        
    }

    async updatetext_handler(){
        if(this.model == null || this.model == undefined){
            return;
        }
        
        if(this.model.api_use){
            if(Content_Model.apiData.has(this.model.apiname)){
                const resultdata = Content_Model.apiData.get(this.model.apiname);
                const dataary = this.getValueByPath(resultdata, this.model.apiarray) as any;
                if(dataary != null && dataary != undefined){
                    this.model.svg_path = dataary;
                    await this.loadSVG();
                }
            }
        }
    }

    getValueByPath<T>(json: Record<string, any>, path: string): T | undefined {
        const parts = path.match(/\[([^\]]+)\]/g) || [];
        return parts.reduce((acc: any, part: string) => {
            const key = part.replace(/\[|\]/g, '');
            return acc && (acc.hasOwnProperty(key) ? acc[key] : acc[Number(key)]);
        }, json) as T;
    }

    exit(): void {
        gsap.killTweensOf(this);

        if(this.model != null && this.model.api_use){
            emitter.off('updatetext', this.updateTextHandler);
        }
        
        if(this.model?.event_use){
            if(this.model.event_type >= 1){
                this.removeEventListener('click',       this.updateTextClickHandler);
                this.removeEventListener('touchstart',  this.updateTextTouchHandler);                
            }
        }

        if(this.timeline != null){
            this.timeline.kill();
            this.timeline.clear();
            this.timeline = null;
        }

        if(this.model != null){
            this.model.play_component = null;
        }
    }

    playAni(){
        if(this.model == null) return;

        if(this.model.aniplaymodel != null && this.model.aniplaymodel.anilist != null && this.model.aniplaymodel.anilist.length > 0){
            setTimeout(() => {
                this.drawView();
                this.runAni();
            }, Number(this.model.aniplaymodel.anilist[0].delay) * 1000);
        }else{
            this.drawView();
        }
    }

    runAni(){
        if(this.model == null) return;

        if(this.timeline != null){
            this.timeline.kill();
            this.timeline.clear();
            this.timeline = null;
        }
        this.timeline = gsap.timeline({repeat:Number(this.model.aniplaymodel.repeat), repeatDelay:Number(this.model.aniplaymodel.repeatDelay), yoyo:Boolean(this.model.aniplaymodel.yoyo)});

        let move_posx   = Number(this.model.transx);
        let move_posy   = Number(this.model.transy);
        let move_rotate = Number(this.model.rotate);
        
        let move_rotatex = 0;
        let move_rotatey = 0;
        let move_skewx   = 0;
        let move_skewy   = 0;

        this.model.aniplaymodel.anilist.forEach((ani, index)=>{
            move_posx += Number(ani.movex);
            move_posy += Number(ani.movey);
            move_rotate += Number(ani.rotate);
            move_rotatex += Number(ani.rotatex);
            move_rotatey += Number(ani.rotatey);
            move_skewx += Number(ani.skewx);
            move_skewy += Number(ani.skewy);
            
            const aniprop: {[key: string]: string|number|boolean} = { delay: ani.delay, duration:ani.duration, x:move_posx, y:move_posy
                                                            , scaleX:ani.scalex, scaleY:ani.scaley
                                                            , rotationX:move_rotatex, rotationY:move_rotatey, rotation:move_rotate
                                                            , skewX:move_skewx, skewY:move_skewy        
                                                            , alpha:ani.alpha, ease:`${ani.ease}.${ani.easetype}` 
                                                            , transformOrigin: `${ani.centerx}% ${ani.centery}%`
                                                            , force3D: true, willChange: "transform"};
            ani.proplist.forEach(prop=>{
                aniprop[prop.propname] = prop.propvalue;
            });
            this.timeline!.to(this, aniprop);
        });        
    }

    async drawView(){
        if(this.model == null || this.model == undefined){
            return;
        }

        this.style.backgroundColor  = "#00000000";
        this.style.width  = this.model.width+"px";
        this.style.height = this.model.height+"px";
        this.style.opacity = String(this.model.opacity);
        
        const hasSeparateRadius = this.model.border_radius_tl > 0 || 
                                this.model.border_radius_tr > 0 || 
                                this.model.border_radius_bl > 0 || 
                                this.model.border_radius_br > 0;
        
        if (hasSeparateRadius) {
            this.style.borderRadius = `${this.model.border_radius_tl}px ${this.model.border_radius_tr}px ${this.model.border_radius_br}px ${this.model.border_radius_bl}px`;
        } else {
            this.style.borderRadius = `${this.model.border_radius}px`;
        }

        //shadow 처리
        if(this.model.shadow_use){
            this.style.filter = `drop-shadow(${this.model.shadow_hori}px ${this.model.shadow_verti}px ${this.model.shadow_blur}px ${this.model.shadow_color})`;
        }else{
            this.style.filter = "";
        }

        await this.loadSVG();
        
        this.style.transform = `translateX(${this.model.transx}px) translateY(${this.model.transy}px) rotate(${this.model.rotate}deg)`;
    }

    async loadSVG(){
        if(!this.model?.svg_path || this.model.svg_path.length === 0){
            if(this.innerHTML !== ""){
                this.innerHTML = "";
            }
            return;
        }

        // 색상 설정을 문자열로 변환하여 비교용으로 사용
        const colorSettings = `${this.model.svg_color_use}_${this.model.svg_color_type}_${this.model.svg_fill_color}`;
        
        // 같은 SVG 파일과 색상 설정이면 스킵
        if(this.lastSvgPath === this.model.svg_path && this.lastColorSettings === colorSettings){
            return;
        }

        try {
            let svgContent: string;
            
            // 캐시 확인
            const cacheKey = this.model.svg_path;
            if(Play_SVG.svgCache.has(cacheKey)){
                svgContent = Play_SVG.svgCache.get(cacheKey)!;
            } else {
                // 네트워크에서 로딩
                if(this.model.svg_path.toLowerCase().startsWith("http")){
                    const response = await fetch(this.model.svg_path);
                    svgContent = await response.text();
                } else {
                    const response = await fetch(this.model.svg_path);
                    svgContent = await response.text();
                }
                
                // 캐시에 저장 (원본 SVG 내용)
                Play_SVG.svgCache.set(cacheKey, svgContent);
            }

            // 색상 적용
            if(this.model.svg_color_use){
                svgContent = this.applySVGColors(svgContent);
            }

            const svgElement = this.createSVGElement(svgContent);
            
            // 깜빡임 방지: 기존 SVG와 다를 때만 교체
            const currentSvg = this.querySelector('svg');
            if(!currentSvg){
                // 첫 로딩 시
                this.appendChild(svgElement);
            } else {
                // SVG 내용이 실제로 다를 때만 교체
                const newContent = svgElement.innerHTML;
                const currentContent = currentSvg.innerHTML;
                
                if(newContent !== currentContent){
                    // 기존 SVG의 속성 유지하면서 내용만 교체
                    currentSvg.innerHTML = newContent;
                    
                    // 색상이 변경된 경우 fill 속성도 업데이트
                    if(this.model.svg_color_use){
                        const newSvgAttrs = svgElement.attributes;
                        for(let i = 0; i < newSvgAttrs.length; i++){
                            const attr = newSvgAttrs[i];
                            if(attr.name !== 'style'){ // style은 createSVGElement에서 설정되므로 제외
                                currentSvg.setAttribute(attr.name, attr.value);
                            }
                        }
                    }
                }
            }
            
            // 현재 상태 저장
            this.lastSvgPath = this.model.svg_path;
            this.lastColorSettings = colorSettings;

        } catch (error) {
            console.error("SVG 로드 실패:", error);
            if(this.innerHTML !== ""){
                this.innerHTML = "";
            }
        }
    }

    applySVGColors(svgContent: string): string {
        let modifiedSvg = svgContent;
        
        if(this.model?.svg_color_use && this.model?.svg_fill_color){
            const colorType = this.model.svg_color_type || "color";
            
            if (colorType === "gradient") {
                // 그라데이션 처리
                const gradientId = `svg-gradient-${Date.now()}`;
                const parsedGradient = this.parseCSSGradient(this.model.svg_fill_color);
                
                if (parsedGradient) {
                    const gradientType = parsedGradient.type === 'radial' ? 'radialGradient' : 'linearGradient';
                    let gradientAttrs = '';
                    
                    if (parsedGradient.type === 'radial') {
                        gradientAttrs = `cx="50%" cy="50%" r="50%"`;
                    } else {
                        // 선형 그라데이션 각도 처리
                        const angle = parsedGradient.angle || 0;
                        const radians = (angle * Math.PI) / 180;
                        const x1 = 50 + 50 * Math.cos(radians + Math.PI);
                        const y1 = 50 + 50 * Math.sin(radians + Math.PI);
                        const x2 = 50 + 50 * Math.cos(radians);
                        const y2 = 50 + 50 * Math.sin(radians);
                        gradientAttrs = `x1="${x1}%" y1="${y1}%" x2="${x2}%" y2="${y2}%"`;
                    }
                    
                    let gradientStops = '';
                    parsedGradient.stops.forEach(stop => {
                        gradientStops += `<stop offset="${stop.position}%" stop-color="${stop.color}"/>`;
                    });
                    
                    const gradientDef = `<defs><${gradientType} id="${gradientId}" ${gradientAttrs}>${gradientStops}</${gradientType}></defs>`;
                    
                    // SVG에 gradient defs 추가
                    modifiedSvg = modifiedSvg.replace(/<svg([^>]*)>/i, `<svg$1>${gradientDef}`);
                    
                    // fill 속성을 gradient URL로 변경
                    modifiedSvg = modifiedSvg.replace(/fill="[^"]*"/g, `fill="url(#${gradientId})"`);
                    modifiedSvg = modifiedSvg.replace(/style="fill:[^;"]*;?/g, `style="fill:url(#${gradientId});`);
                } else {
                    // 그라데이션 파싱 실패 시 단색으로 처리
                    modifiedSvg = modifiedSvg.replace(/fill="[^"]*"/g, `fill="${this.model.svg_fill_color}"`);
                    modifiedSvg = modifiedSvg.replace(/style="fill:[^;"]*;?/g, `style="fill:${this.model.svg_fill_color};`);
                }
            } else {
                // 단색 처리
                modifiedSvg = modifiedSvg.replace(/fill="[^"]*"/g, `fill="${this.model.svg_fill_color}"`);
                modifiedSvg = modifiedSvg.replace(/style="fill:[^;"]*;?/g, `style="fill:${this.model.svg_fill_color};`);
            }
        }
        
        return modifiedSvg;
    }

    // CSS 그라데이션 문자열을 파싱하는 메서드
    parseCSSGradient(cssGradient: string): { type: string, angle?: number, stops: Array<{color: string, position: number}> } | null {
        try {
            // linear-gradient 또는 radial-gradient 추출
            const isRadial = cssGradient.includes('radial-gradient');
            const type = isRadial ? 'radial' : 'linear';
            
            // 괄호 안의 내용 추출
            const match = cssGradient.match(/(?:linear|radial)-gradient\((.+)\)$/);
            if (!match) return null;
            
            const content = match[1];
            
            // 색상 stop들을 정확하게 분리하기 위해 더 정교한 방법 사용
            const colorStops: Array<{color: string, position: number}> = [];
            let angle = 180;
            
            // 각도 추출 (Ndeg 또는 CSS 키워드 방향 to right/to bottom 등)
            const angleMatch = content.match(/(-?[\d.]+)deg/);
            if (angleMatch) {
                angle = parseFloat(angleMatch[1]);
            } else {
                const dirMatch = content.match(/to\s+(top|bottom|left|right)(?:\s+(top|bottom|left|right))?/i);
                if (dirMatch) {
                    const dirs = [dirMatch[1], dirMatch[2]].filter(Boolean).map(d => d.toLowerCase());
                    const cardinal: Record<string, number> = { top: 0, right: 90, bottom: 180, left: 270 };
                    if (dirs.length === 1) {
                        angle = cardinal[dirs[0]];
                    } else {
                        const has = (d: string) => dirs.indexOf(d) !== -1;
                        if (has('top') && has('right')) angle = 45;
                        else if (has('bottom') && has('right')) angle = 135;
                        else if (has('bottom') && has('left')) angle = 225;
                        else if (has('top') && has('left')) angle = 315;
                    }
                }
            }
            
            // HEX 색상 stop 추출 - #RRGGBB position% 또는 #RRGGBBAA position% 형태
            const hexMatches = content.matchAll(/#([0-9a-fA-F]{6}([0-9a-fA-F]{2})?)\s*(\d+)%/g);
            for (const match of hexMatches) {
                const hex = match[1];
                const position = parseInt(match[3]);
                
                // HEX를 RGB/RGBA로 변환
                let color: string;
                if (hex.length === 8) {
                    // 8자리 HEX (RRGGBBAA)
                    const r = parseInt(hex.slice(0, 2), 16);
                    const g = parseInt(hex.slice(2, 4), 16);
                    const b = parseInt(hex.slice(4, 6), 16);
                    const a = parseInt(hex.slice(6, 8), 16) / 255;
                    color = `rgba(${r}, ${g}, ${b}, ${a})`;
                } else {
                    // 6자리 HEX (RRGGBB)
                    const r = parseInt(hex.slice(0, 2), 16);
                    const g = parseInt(hex.slice(2, 4), 16);
                    const b = parseInt(hex.slice(4, 6), 16);
                    color = `rgb(${r}, ${g}, ${b})`;
                }
                
                console.log('[Play_SVG] HEX color extraction:', {
                    original: `#${hex}`,
                    converted: color,
                    position: position
                });
                
                colorStops.push({
                    color: color,
                    position: position
                });
            }
            
            // RGB/RGBA 색상 stop 추출 - rgba(r, g, b, a) position% 또는 rgb(r, g, b) position% 형태
            const colorMatches = content.matchAll(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+))?\s*\)\s*(\d+)%/g);
            for (const match of colorMatches) {
                const r = match[1];
                const g = match[2];
                const b = match[3];
                const a = match[4]; // 알파값 (없을 수도 있음)
                const position = parseInt(match[5]);
                
                const color = a !== undefined ? `rgba(${r}, ${g}, ${b}, ${a})` : `rgb(${r}, ${g}, ${b})`;
                colorStops.push({
                    color: color,
                    position: position
                });
            }
            
            return { type, angle, stops: colorStops };
        } catch (error) {
            console.error('CSS gradient parsing error:', error);
            return null;
        }
    }

    createSVGElement(svgContent: string): SVGElement {
        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(svgContent, "image/svg+xml");
        const svgElement = svgDoc.querySelector('svg') as SVGElement;
        
        if(svgElement){
            svgElement.style.width = "100%";
            svgElement.style.height = "100%";
        }
        
        return svgElement;
    }

    resultHttp(a_id:number, result:string, obj:object){
        
    }

    disconnectedCallback(): void {
        this.exit();
    }
}

export default Play_SVG
customElements.define('play-svg', Play_SVG);